import { Flex, InputGroup, InputRightAddon, NumberInput, NumberInputField, Text } from "@chakra-ui/react";
import { useEffect, useRef, useState } from "react";
import { CompactNumberStepper } from "../../../../../common/input/CompactNumberStepper";

const MIN_STEP = 1;
const MAX_STEP = 100;

// Chakra's default filter drops the comma, which would turn a Norwegian "12,5" into 125. `update`
// handles the separator; this only decides which keystrokes are allowed to reach it.
const isWeightCharacter = (char: string) => /[\d.,]/.test(char);

interface Props {
  value: number | null | undefined;
  isDisabled: boolean;
  isInvalid?: boolean;
  ariaLabel: string;
  /**
   * Table form: no border until the field is pointed at, "%" as plain text, and a stepper beside it.
   * The bordered default is what a form panel wants, so the criterion drawer leaves this off.
   */
  isQuiet?: boolean;
  onChange: (weight: number | undefined) => void;
}

/**
 * The inline "40 %" cell used by both criterion and sub-criterion rows. A local string buffer absorbs
 * empty and half-typed values so only well-formed numbers reach the save coordinator; range checking
 * lives on the BFF (FieldErrorCode_OutOfRange). Weights are integer-only on this form, which side-steps
 * locale display, float drift and BigDecimal precision mismatch with the BFF.
 *
 * NumberInput wraps that buffer rather than replacing it: Chakra owns the spinbutton semantics — one
 * tab stop, arrow keys, PageUp/PageDown, steppers out of the tab order — while the buffer keeps the
 * comma tolerance a Norwegian keyboard needs, in the display as well as in the parse.
 */
export const WeightInput = ({ value, isDisabled, isInvalid, ariaLabel, isQuiet, onChange }: Props) => {
  const [local, setLocal] = useState<string>(value != null ? String(value) : "");
  // A parent that stores the rounded number synchronously (a form, as opposed to the debounced save
  // coordinator) would otherwise hand it straight back mid-keystroke and swallow the "," in "12,5".
  const isFocused = useRef(false);

  useEffect(() => {
    if (isFocused.current) return;
    setLocal(value != null ? String(value) : "");
  }, [value]);

  // The parent funnels onChange into the coordinator with a debounce, so every keystroke propagates and
  // the coordinator collapses them — a ref keeps us calling the latest parent callback.
  const onChangeRef = useRef(onChange);
  useEffect(() => {
    onChangeRef.current = onChange;
  }, [onChange]);

  const update = (raw: string) => {
    setLocal(raw);
    if (raw === "") {
      onChangeRef.current(undefined);
      return;
    }
    const parsed = Number.parseFloat(raw.replace(",", "."));
    if (!Number.isFinite(parsed)) return;
    onChangeRef.current(Math.round(parsed));
  };

  const spinner = (
    // keepWithinRange stays on its default, so the steppers hold to 1–100 while what is typed is left
    // alone: that range is the BFF's to judge, and it answers with a field error.
    //
    // The panel's field is md to stand level with the name input beside it; the table's is sm, where it
    // has to read as a line of the row rather than a form control.
    <NumberInput
      size={isQuiet ? "sm" : "md"}
      min={MIN_STEP}
      max={MAX_STEP}
      step={1}
      value={local}
      onChange={update}
      isDisabled={isDisabled}
      isInvalid={isInvalid}
      clampValueOnBlur={false}
      isValidCharacter={isWeightCharacter}
      w={isQuiet ? "5rem" : undefined}
      flex={isQuiet ? undefined : "1"}
      // An <input> carries a ~20-character min-content width, which a flex item honours unless told
      // otherwise — without this the control refuses to shrink and pushes straight out of the panel.
      minW="0"
      // Narrower than the 1.5rem default, since a chevron needs less room than Chakra's triangle. The
      // theme derives the field's right padding from this, so the number keeps its distance by itself.
      sx={{ "--number-input-stepper-width": "1rem" }}
    >
      <NumberInputField
        aria-label={ariaLabel}
        onFocus={() => {
          isFocused.current = true;
        }}
        onBlur={() => {
          isFocused.current = false;
          setLocal(value != null ? String(value) : "");
        }}
        {...(isQuiet
          ? {
              // Reads as text until it is pointed at or focused, which is what keeps the table from
              // looking like a form. Chakra's focus ring is left to do its own job.
              rounded: "md",
              textAlign: "right" as const,
              borderColor: "transparent",
              bg: "transparent",
              placeholder: "—",
              _hover: { borderColor: "smBorder" },
            }
          : {
              // InputGroup only squares off children it recognises, and NumberInputField is not one of
              // them — so the corner facing the "%" addon is flattened here instead.
              roundedLeft: "md",
              roundedRight: "0",
            })}
      />
      <CompactNumberStepper upTestId="weight-step-up" downTestId="weight-step-down" />
    </NumberInput>
  );

  if (!isQuiet) {
    // The group wraps the spinner rather than the other way round: the steppers anchor to the nearest
    // NumberInput, so this keeps them inside the field instead of over the "%" addon.
    return (
      <InputGroup size="md" w="full">
        {spinner}
        <InputRightAddon rounded="md" roundedLeft="0" px="2">
          %
        </InputRightAddon>
      </InputGroup>
    );
  }

  return (
    <Flex align="center" gap="1">
      {spinner}
      <Text fontSize="sm" color="smMuted">
        %
      </Text>
    </Flex>
  );
};
